/**
 * ServicesSection Component
 * Design: "Light with Dark Accents"
 * White cards on #F8F9FC background, colored icon boxes, slate text
 */
import { Search, BarChart3, Share2, Target, Lightbulb, GraduationCap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const serviceIcons = [Target, BarChart3, Share2, Search, Lightbulb, GraduationCap];
const serviceColors = ["blue", "blue", "emerald", "emerald", "blue", "emerald"];

const colorMap = {
  blue: {
    icon: "bg-blue-50 text-blue-600 border border-blue-100",
    tag: "bg-blue-50 text-blue-600 border border-blue-100",
    accent: "#2563EB",
    ring: "ring-blue-200",
  },
  emerald: {
    icon: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    tag: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    accent: "#047857",
    ring: "ring-emerald-200",
  },
};

export default function ServicesSection() {
  const { t } = useLanguage();

  return (
    <section id="services" className="py-24 lg:py-32 relative" style={{ background: "#ffffff" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16">
          <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block font-sora">
            {t.services.sectionLabel}
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-sora font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight max-w-xl">
              {t.services.headline1}<br />
              <span className="text-blue-600">{t.services.headline2}</span>
            </h2>
            <p className="text-slate-700 max-w-md font-inter leading-relaxed">
              {t.services.subtext}
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.items.map((service, i) => {
            const colorKey = serviceColors[i] as keyof typeof colorMap;
            const colors = colorMap[colorKey];
            const Icon = serviceIcons[i];
            return (
              <div
                key={service.title}
                className={`bg-[#F8F9FC] border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${i === 0 ? `ring-2 ${colors.ring}` : ""}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${colors.icon}`}>
                  <Icon size={22} />
                </div>
                <h3 className="font-sora font-bold text-xl text-slate-900 mb-1">{service.title}</h3>
                <p className="text-sm font-sora font-semibold mb-3" style={{ color: colors.accent }}>
                  {service.subtitle}
                </p>
                <p className="text-slate-700 text-sm leading-relaxed mb-5 font-inter">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`text-xs px-2.5 py-1 rounded-full font-sora font-medium ${colors.tag}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
